import {
  ApplicationStep,
  ApplicationType,
  ErroredStatus,
  HeardAboutInfo,
  RetirementAccountType,
} from '@tradingblock/types';
import { BasicSelect, OptionType, Tooltip, useInput } from '@tradingblock/components';
import React, { useCallback, useMemo } from 'react';
import { useApplicationsActions, useApplicationsData } from '../useApplications';
import _ from 'lodash';

export const stepOptions: OptionType<ApplicationStep | undefined>[] = [
  { value: 'All', label: 'Step: All', data: undefined },
  { value: 'Secure Account', label: 'Secure Account', data: ApplicationStep.SecureAccount },
  { value: 'Clearer Type', label: 'Clearer Type', data: ApplicationStep.ClearerType },
  { value: 'Account Type', label: 'Account Type', data: ApplicationStep.AccountType },
  { value: 'Investing Profile', label: 'Investing Profile', data: ApplicationStep.InvestingProfile },
  { value: 'Account Information', label: 'Account Information', data: ApplicationStep.AccountInformation },
  { value: 'Beneficiaries', label: 'Beneficiaries', data: ApplicationStep.Beneficiaries },
  { value: 'Agreements', label: 'Agreements', data: ApplicationStep.Agreements },
  { value: 'Sign', label: 'Sign', data: ApplicationStep.Sign },
];

export const typeOptions: OptionType<ApplicationType | undefined>[] = [
  { value: 'All', label: 'Type: All', data: undefined },
  { value: 'Individual', label: 'Individual', data: ApplicationType.Individual },
  { value: 'Joint', label: 'Joint', data: ApplicationType.Joint },
  { value: 'Entity', label: 'Entity', data: ApplicationType.Entity },
  { value: 'UGMA', label: 'UGMA', data: ApplicationType.UGMA },
];

export const errorOptions: OptionType<ErroredStatus | undefined>[] = [
  { value: 'Errors: All', label: 'Errors: All', data: undefined },
  { value: 'Errored', label: 'Errored', data: ErroredStatus.Errored },
  { value: 'Not Errored', label: 'Not Errored', data: ErroredStatus.NotErrored },
];

export const iraAccountTypes: OptionType<RetirementAccountType | undefined>[] = [
  { value: 'All', label: 'IRA Type: All', data: undefined },
  { value: 'Traditional IRA', label: 'Traditional IRA', data: RetirementAccountType.Traditional },
  { value: 'Roth IRA', label: 'Roth IRA', data: RetirementAccountType.Roth },
  { value: 'SEP IRA', label: 'SEP IRA', data: RetirementAccountType.SEP },
  { value: 'Simple IRA', label: 'Simple IRA', data: RetirementAccountType.Simple },
  { value: 'CoverDell IRA', label: 'CoverDell IRA', data: RetirementAccountType.CoverDell },
  { value: 'Beneficiary IRA', label: 'Beneficiary IRA', data: RetirementAccountType.Beneficiary },
  { value: 'Rollover IRA', label: 'Rollover IRA', data: RetirementAccountType.Rollover },
];
export const ApplicationsFilters: React.FunctionComponent<{ heardAboutUsArray: HeardAboutInfo[] }> = ({
  heardAboutUsArray,
}) => {
  const { search, steps, types, errors, iraType, repId } = useApplicationsData();
  const { setSearch, setRepId: onRepIdSearch, setStep, setType, setErrorStatus, setIraType } = useApplicationsActions();

  const onSearchSubmit = useCallback((search: string) => {
    const trimmedSearch = search ? search.trim() : '';
    setSearch(trimmedSearch);
  }, [setSearch]);

  const [searchValue, setSearchValue, inputProps] = useInput(undefined, { onEnter: onSearchSubmit });

  const onClearSearch = useCallback(() => {
    onSearchSubmit('');
    setSearchValue('');
  }, []);

  const currentSearch = useMemo(() => {
    if (!_.isUndefined(searchValue)) {
      return searchValue;
    }
    return search || '';
  }, [search, searchValue]);

  const currentStep = useMemo(() => {
    const firstStep = _.first(steps);
    if (firstStep) {
      return firstStep;
    }

    const defaultStep = _.first(stepOptions);
    return defaultStep ? defaultStep.label : '';
  }, [steps]);

  const currentType = useMemo(() => {
    const firstType = _.first(types);
    if (firstType) {
      return firstType;
    }

    const defaultType = _.first(typeOptions);
    return defaultType ? defaultType.label : '';
  }, [types]);

  const currentErrorStatus = useMemo(() => {
    if (errors && errors.length > 0) {
      // if NotErrored is selected, we want to show 'Not Errored' instead of 'NotErrored'
      if (errors.length === 1 && errors[0] === ErroredStatus.NotErrored) {
        return 'Not Errored';
      }
      return errors;
    }
    return 'Errors: All';
  }, [errors]);

  const currentIraType = useMemo(() => {
    if (iraType && iraType.length > 0) {
      return iraType;
    }
    return 'IRA Type: All';
  }, [iraType]);

  const onStepChange = useCallback((val: OptionType | OptionType[] | null | undefined, { action }) => {
    const steps = _.isNil(val) ? [] : _.isArray(val) ? val : [val];
    setStep(steps.filter(s => s.data !== undefined).map(s => s.data));
  }, []);

  const onTypeChange = useCallback((val: OptionType | OptionType[] | null | undefined, { action }) => {
    const types = _.isNil(val) ? [] : _.isArray(val) ? val : [val];
    setType(types.filter(t => t.data !== undefined).map(t => t.data));
  }, []);

  const onErrorStatusChange = useCallback((val: OptionType | OptionType[] | null | undefined, { action }) => {
    const errors = _.isNil(val) ? [] : _.isArray(val) ? val : [val];
    setErrorStatus(errors.filter(e => e.data !== undefined).map(e => e.data));
  }, []);

  const onIraTypeChange = useCallback((val: OptionType | OptionType[] | null | undefined, { action }) => {
    const iraTypes = _.isNil(val) ? [] : _.isArray(val) ? val : [val];
    setIraType(iraTypes.filter(t => t.data !== undefined).map(t => t.data));
  }, []);

  function extractValue(s: string): string {
    const match = s.match(/\(([^)]+)\)/);
    return match ? match[1] : '';
  }

  const repCodeArray: { repCode: string; repId: number }[] = useMemo(() => {
    const repCodeArr: { repCode: string; repId: number }[] = [];

    heardAboutUsArray.forEach(obj => {
      if (obj.representatives)
        obj.representatives.forEach(rep =>
          repCodeArr.push({
            repCode: extractValue(rep.description),
            repId: rep.repId,
          })
        );
    });

    return [...repCodeArr];
  }, [heardAboutUsArray]);

  const onRepCodeSearchSubmit = useCallback(
    (repCode: string) => {
      if (repCode.trim().length === 0) {
        onRepIdSearch(undefined);
      } else {
        const foundRepCodeObj = repCodeArray.find(repCodeObj => repCodeObj.repCode === repCode.trim());
        if (foundRepCodeObj) onRepIdSearch([foundRepCodeObj.repId.toString()]);
        else onRepIdSearch(undefined);
      }
    },
    [repCodeArray]
  );

  const [repCodeValue, setRepCodeValue, repCodeSearchInputProps] = useInput(undefined, {
    onEnter: onRepCodeSearchSubmit,
  });

  const currentRepId = useMemo(() => {
    let foundRepId = null;
    if (!_.isUndefined(repId)) {
      if (typeof repId === 'string') foundRepId = repId;
      else if (typeof repId === 'object' && repId.length > 0) foundRepId = repId[0];
    }

    if (foundRepId) {
      const foundRepCode = repCodeArray.find(r => r.repId == foundRepId);
      if (foundRepCode) return foundRepCode.repCode;
    }

    return repCodeValue || '';
  }, [repId, repCodeValue]);

  const onClearRepCodeSearch = useCallback(() => {
    onRepCodeSearchSubmit('');
    setRepCodeValue('');
  }, []);

  return (
    <div className="table-toolbar table-toolbar-top">
      <div className="table-toolbar">
        <div className="table-toolbar-primary mr-2">
          <Tooltip
            content="You can search for name, username, email, or account ID."
            effect="solid"
            id="app-search-tooltip"
            type="info"
          >
            <input
              className="input input-dark input-tall"
              type="text"
              placeholder={'Search...'}
              value={currentSearch}
              {...inputProps}
            />
          </Tooltip>
          {currentSearch && (
            <i
              className="fa fa-times fa-2x"
              onClick={onClearSearch}
              style={{ color: 'var(--blue)', cursor: 'pointer', paddingLeft: '0.32em' }}
            />
          )}
        </div>
        <div className="table-toolbar-primary">
          <Tooltip content="You can search for rep code." effect="solid" id="app-repcode-tooltip" type="info">
            <input
              className="input input-dark input-tall w-100"
              type="text"
              placeholder={'Search by rep code...'}
              value={currentRepId}
              {...repCodeSearchInputProps}
            />
          </Tooltip>
          {currentRepId && (
            <i
              className="fa fa-times fa-2x"
              onClick={onClearRepCodeSearch}
              style={{ color: 'var(--blue)', cursor: 'pointer', paddingLeft: '0.32em' }}
            />
          )}
        </div>
      </div>
      <div className="table-toolbar-secondary">
        <BasicSelect
          onChange={onTypeChange}
          placeholder={
            currentType[0].toUpperCase() +
            currentType
              .slice(1)
              .replace(/([A-Z])/g, ' $1')
              .trim()
          }
          className="btn btn-dark btn-tall dropdown-toggle text-left"
          options={typeOptions}
          isClearable={false}
        />
        <BasicSelect
          onChange={onStepChange}
          placeholder={
            currentStep[0].toUpperCase() +
            currentStep
              .slice(1)
              .replace(/([A-Z])/g, ' $1')
              .trim()
          }
          className="btn btn-dark btn-tall dropdown-toggle text-left"
          options={stepOptions}
          isClearable={false}
        />
        <BasicSelect
          onChange={onErrorStatusChange}
          placeholder={currentErrorStatus}
          className="btn btn-dark btn-tall dropdown-toggle text-left"
          options={errorOptions}
          isClearable={false}
        />
        <BasicSelect
          onChange={onIraTypeChange}
          placeholder={currentIraType}
          className="btn btn-dark btn-tall dropdown-toggle text-left"
          options={iraAccountTypes}
          isClearable={false}
        />
      </div>
    </div>
  );
};
